import type { DayData, Segment } from './types'
import { timeLabel } from './time'

/** Calendar-day context. Dragging the window pans the detail ruler, not playback. */
export function DayOverview({
  data,
  segments,
  view,
  onChange,
}: {
  data: DayData
  segments: Segment[]
  view: { start: number; end: number }
  onChange: (view: { start: number; end: number }) => void
}) {
  const span = data.end - data.start
  const length = view.end - view.start
  const percent = (value: number) => ((value - data.start) / span) * 100
  function move(start: number) {
    start = Math.max(data.start, Math.min(data.end - length, start))
    onChange({ start, end: start + length })
  }
  return (
    <section className="day-overview" aria-label="Calendar day overview">
      <div
        className="overview-track"
        role="slider"
        tabIndex={0}
        aria-label="Visible timeline range"
        aria-valuemin={data.start}
        aria-valuemax={data.end - length}
        aria-valuenow={view.start}
        aria-valuetext={`${timeLabel(view.start, data.timezone, true, true)} to ${timeLabel(view.end, data.timezone, true, true)}`}
        onKeyDown={(event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
          event.preventDefault()
          event.stopPropagation()
          move(
            event.key === 'Home'
              ? data.start
              : event.key === 'End'
                ? data.end - length
                : view.start + ((event.key === 'ArrowLeft' ? -1 : 1) * length) / 4,
          )
        }}
      >
        {segments.map((s) => (
          <i
            key={`${s.recording.id}-${s.start}`}
            className="overview-coverage"
            style={{
              left: `${percent(Math.max(data.start, s.start))}%`,
              width: `${(Math.max(0, Math.min(data.end, s.end) - Math.max(data.start, s.start)) / span) * 100}%`,
            }}
          />
        ))}
        <div
          className="overview-window"
          style={{
            left: `min(${percent(view.start)}%, calc(100% - 8px))`,
            width: `${(length / span) * 100}%`,
          }}
        />
      </div>
    </section>
  )
}
